export type VideoCodecId = "h264" | "h265" | "av1";
export type VideoTransportId = "webcodecs" | "webrtc";
export type VideoColorMode = "color" | "grayscale";

/**
 * Cadrage de l'encodage côté agent :
 *   "fit"  → le bureau entier est downscalé vers le viewport (défaut historique).
 *   "real" → mode taille réelle 1:1 : l'agent crope `sourceRect` du bureau (pixels
 *            natifs) et le copie 1:1 dans la sortie ; pan via `video:viewrect`.
 * Contrat figé partagé entre opérateur, relais Rust et agent C#.
 */
export type FramingMode = "fit" | "real";

export interface VideoPreset {
  id: string;
  label: string;
  codec: VideoCodecId;
  transport: VideoTransportId;
  mode: "annexb";
  recommended: boolean;
  experimental: boolean;
  description: string;
}

export interface QualityProfile {
  id: string;
  label: string;
  fps: number;
  maxWidth: number;
  bitrate: number;
  description: string;
  /** Intention de contenu du profil. "text" = bureautique/terminal (pousse AV1/HEVC
   *  via WS-I `pickCodecForProfile`, favorable aux tuiles texte) ; "mixed" = vidéo/
   *  animation. Absent = comportement historique (content "text" par défaut WS-I). */
  content?: VideoContentProfile;
  /** Préférence couleur du profil (ex. "Texte éco" terminal → grayscale, débit mini).
   *  Le mode "free" garde son propre colorMode. Absent = "color" (inchangé). */
  preferColorMode?: VideoColorMode;
}

export interface VideoStartParams {
  fps: number;
  maxWidth: number;
  bitrate: number;
  codec: VideoCodecId;
  mode: "annexb";
  colorMode?: VideoColorMode;
  /** WS-N — 4:4:4 chroma pleine résolution (texte net). Absent = 4:2:0 actuel.
   * Posé seulement après le gate dur isConfigSupported require-hardware. */
  chroma444?: boolean;
  /** WS-K — mixed-mode tuiles texte lossless. Absent = plein cadre H264 actuel.
   * Posé seulement si l'agent annonce videoMixedMode ET le worker décode est actif. */
  mixedMode?: boolean;
  /** WS-M — cache de glyphes (mode REF : tuiles texte récurrentes rejouées du cache).
   * Sur-couche de mixedMode. Absent = format WS-K actuel (aucun hash, aucun REF). Posé
   * seulement si l'agent annonce videoGlyphCache ET que mixedMode est lui-même posé. */
  glyphCache?: boolean;
  /** Largeur du viewport opérateur en pixels PHYSIQUES (round(cssW × dpr)). */
  viewportW?: number;
  /** Hauteur du viewport opérateur en pixels PHYSIQUES (round(cssH × dpr)). */
  viewportH?: number;
  /** Cadrage initial demandé ("fit" par défaut). */
  framingMode?: FramingMode;
}

export type CodecFeatureMap = Partial<Record<"codecH264" | "codecH265" | "codecAV1" | "videoWebRtc", boolean>>;

export const VIDEO_PRESETS: VideoPreset[] = [
  {
    id: "h264-webcodecs",
    label: "H.264",
    codec: "h264",
    transport: "webcodecs",
    mode: "annexb",
    recommended: true,
    experimental: false,
    description: "Compatibilité maximale Windows/macOS/web, accélération matérielle et faible latence.",
  },
  {
    id: "h265-webcodecs",
    label: "HEVC",
    codec: "h265",
    transport: "webcodecs",
    mode: "annexb",
    recommended: false,
    experimental: false,
    description: "Meilleure compression si l'agent et le navigateur supportent explicitement HEVC.",
  },
  {
    id: "av1-webcodecs",
    label: "AV1",
    codec: "av1",
    transport: "webcodecs",
    mode: "annexb",
    recommended: false,
    experimental: true,
    description: "Réservé aux futurs encodeurs hardware détectés strictement côté agent.",
  },
  {
    id: "h264-webrtc",
    label: "WebRTC",
    codec: "h264",
    transport: "webrtc",
    mode: "annexb",
    recommended: false,
    experimental: false,
    description: "Chemin WebRTC à la demande, H.264 uniquement pour garder une négociation robuste.",
  },
];

export const QUALITY_PROFILES: QualityProfile[] = [
  {
    id: "light",
    label: "Léger",
    fps: 10,
    maxWidth: 960,
    bitrate: 800,
    description: "Réseau faible (ADSL, 4G basique) — 960p / 10 fps / 800 kbps",
  },
  {
    id: "standard",
    label: "Standard",
    fps: 15,
    maxWidth: 1280,
    bitrate: 2000,
    description: "Équilibré pour la plupart des connexions — 1280p / 15 fps / 2 Mbps",
  },
  {
    id: "quality",
    label: "Qualité",
    fps: 30,
    maxWidth: 1280,
    bitrate: 6000,
    content: "mixed",
    description: "Haute qualité — 1280p / 30 fps / 6 Mbps",
  },
  {
    id: "max",
    label: "Max",
    fps: 30,
    maxWidth: 1920,
    bitrate: 8000,
    content: "mixed",
    description: "Qualité maximale — 1920p / 30 fps / 8 Mbps (fibre requise)",
  },
  // ── Profils orientés NETTETÉ TEXTE (cf. scripts/legibility-bench) ──────────────
  // Mesure (bench synthétique 2026-06-26) : la résolution NATIVE est le facteur #1
  // de netteté du texte — clamp 1024 = 15% de netteté vs 79-93% en 1920 ; tout
  // downscale sous le natif (1366→29%, 1440→35%) effondre la netteté, à TOUT débit.
  // D'où maxWidth=2560 (natif HiDPI/Retina) + fps bas (le texte bouge peu → bits
  // dans la résolution). 1920 sur bureau natif >1920 = source déjà downscalée
  // AVANT encode → flou résiduel que le glyph lossless ne récupère pas.
  //
  // DÉBIT : ces `bitrate` sont des PLAFONDS de pic (scroll/vidéo), PAS des moyennes.
  // Le débit MOYEN « léger comme TeamViewer » (~420 kbps) vient du SKIP statique +
  // dirty-rects (VideoStaticSkip, HKLM\SOFTWARE\RemoteWS\Video) : écran fixe ≈ 0 kbps.
  // À activer côté agent — sinon un cap bas étrangle le texte (flou). À bas débit,
  // préférer AV1/HEVC (~2× plus efficaces que H264 : H264 à 1,5 Mbps sur 1920 reflou).
  // ⚠️ Sur host sans encodeur HW, l'agent re-clampe à CpuVideoMaxWidth (défaut ceil
  // 1920) ; la borne dure a été relevée 1920→3840 (VideoPerformancePolicy.cs:71) →
  // poser CpuVideoMaxWidth=2560 (registry) sur ces hosts pour honorer 2560 (sinon
  // clamp 1920, défaut prod intact). Hosts avec HW H264 : 2560 honoré direct.
  {
    id: "text-eco",
    label: "Texte éco",
    fps: 8,
    maxWidth: 2560,
    bitrate: 1500,
    content: "text",
    preferColorMode: "grayscale",
    description:
      "Lien lent / data éco — 2560 natif (HiDPI) / 8 fps / 1,5 Mbps plafond. Texte net à bas débit (façon TeamViewer/AnyDesk) via AV1/HEVC + skip statique. Mono/terminal (grayscale, débit mini).",
  },
  {
    id: "text",
    label: "Texte net",
    fps: 12,
    maxWidth: 2560,
    bitrate: 2500,
    content: "text",
    description:
      "Bureautique/terminal — 2560 natif (HiDPI) / 12 fps / 2,5 Mbps plafond. Priorité netteté (zéro downscale) + AV1/HEVC, 4:4:4 si HW (sinon grayscale).",
  },
  {
    id: "balanced",
    label: "Équilibré HD",
    fps: 20,
    maxWidth: 2560,
    bitrate: 3500,
    content: "text",
    description:
      "Usage général net — 2560 natif (HiDPI) / 20 fps / 3,5 Mbps plafond. Compromis netteté + fluidité (navigation, multi-fenêtres).",
  },
  {
    id: "free",
    label: "Libre",
    fps: 15,
    maxWidth: 1280,
    bitrate: 2000,
    description: "Paramètres manuels",
  },
];

// ── ABR — échelle adaptative (Adaptive Bitrate) ───────────────────────────────
// Échelle ordonnée du MEILLEUR (index 0) au PIRE (index 4). `bitrate` en kbps.
// CONTRAT FIGÉ : ces 5 paliers et leurs valeurs sont partagés avec un module
// relais — ne pas modifier sans synchroniser les deux côtés.

export interface AbrRung {
  fps: number;
  maxWidth: number;
  /** Débit cible en kbps. */
  bitrate: number;
}

export const ABR_LADDER: AbrRung[] = [
  { fps: 30, maxWidth: 1920, bitrate: 8000 },
  { fps: 20, maxWidth: 1600, bitrate: 5000 },
  { fps: 15, maxWidth: 1280, bitrate: 3000 },
  { fps: 10, maxWidth: 1280, bitrate: 1500 },
  { fps: 6, maxWidth: 960, bitrate: 800 },
];

export interface AbrUpdateParams {
  bitrate: number;
}

/**
 * Paramètres envoyés par l'ABR automatique. Les paliers gardent fps/maxWidth
 * pour l'affichage et le passage manuel, mais l'automatisme ne modifie que le
 * bitrate pour éviter les redémarrages encodeur en WSS/software.
 */
export function buildAbrUpdateParams(rung: AbrRung): AbrUpdateParams {
  return { bitrate: rung.bitrate };
}

/** Descend d'un palier (vers une qualité plus basse). Borné au pire palier. */
export function abrStepDown(index: number): number {
  return Math.min(ABR_LADDER.length - 1, Math.max(0, index) + 1);
}

/** Monte d'un palier (vers une qualité plus haute). Borné au meilleur palier. */
export function abrStepUp(index: number): number {
  return Math.max(0, Math.min(ABR_LADDER.length - 1, index) - 1);
}

/** Palier le plus proche d'un débit cible (kbps) — pour aligner l'ABR sur le slider manuel. */
export function abrRungForBitrate(bitrate: number): number {
  let best = 0;
  let bestDelta = Number.POSITIVE_INFINITY;
  for (let i = 0; i < ABR_LADDER.length; i++) {
    const delta = Math.abs(ABR_LADDER[i].bitrate - bitrate);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = i;
    }
  }
  return best;
}

export function defaultVideoPreset(): VideoPreset {
  return VIDEO_PRESETS[0];
}

export function findVideoPreset(id: string): VideoPreset {
  return VIDEO_PRESETS.find((preset) => preset.id === id) ?? defaultVideoPreset();
}

export function defaultQualityProfile(): QualityProfile {
  return QUALITY_PROFILES[1]; // standard
}

export function findQualityProfile(id: string): QualityProfile {
  return QUALITY_PROFILES.find((p) => p.id === id) ?? defaultQualityProfile();
}

export function codecFeatureKey(codec: VideoCodecId): keyof CodecFeatureMap {
  if (codec === "h265") return "codecH265";
  if (codec === "av1") return "codecAV1";
  return "codecH264";
}

export function isPresetAgentSupported(preset: VideoPreset, features: CodecFeatureMap | null | undefined): boolean {
  if (!features) return preset.codec === "h264" && preset.transport === "webcodecs";
  const codecSupported = features[codecFeatureKey(preset.codec)] === true || preset.codec === "h264";
  if (!codecSupported) return false;
  if (preset.transport === "webrtc") return features.videoWebRtc === true;
  return true;
}

/**
 * WS-B — preset par défaut tenant compte d'une préférence H265 (kill-switch).
 *
 * Retourne le preset H265 WebCodecs UNIQUEMENT si TOUTES les conditions sont
 * réunies : `h265Default` ON, l'agent annonce `codecH265`, ET le navigateur
 * décode HEVC (`browserH265`). Dans TOUS les autres cas (flag OFF, ou support
 * partiel), retourne `defaultVideoPreset()` — donc strictement identique au
 * comportement actuel (h264). Garantit « flag OFF = identique à main ».
 */
export function preferredDefaultVideoPreset(
  h265Default: boolean,
  agentFeatures: CodecFeatureMap | null | undefined,
  browserH265: boolean,
): VideoPreset {
  if (!h265Default) return defaultVideoPreset();
  const agentH265 = !!agentFeatures && agentFeatures.codecH265 === true;
  if (!agentH265 || !browserH265) return defaultVideoPreset();
  const h265 = VIDEO_PRESETS.find((p) => p.codec === "h265" && p.transport === "webcodecs");
  return h265 ?? defaultVideoPreset();
}

// ── WS-I — politique codec par profil (réseau + contenu) ─────────────────────
// Au lieu de la seule négociation WS-B (H265 par défaut si double support),
// WS-I choisit le preset/codec OPTIMAL selon un PROFIL d'usage. Le profil
// combine le TRANSPORT (firewall/WSS vs WebRTC) et le CONTENU (texte vs mixte).
//
// Cette fonction est OPT-IN : elle n'est appelée par video.tsx QUE si le toggle
// `videoCodecPolicy` est ON. Quand OFF, la sélection de codec reste strictement
// celle d'aujourd'hui (négociation WS-B / choix manuel). De plus, même appelée,
// elle ne réinvente PAS la négociation : tout codec qu'elle recommande est
// validé par `isPresetAgentSupported` (versant agent) ET, via `browserSupport`,
// par le décodage navigateur ; à défaut elle CASCADE vers le codec immédiatement
// inférieur, et en dernier ressort vers `defaultVideoPreset()` (h264).

export type VideoTransportProfile = "wss" | "webrtc";
export type VideoContentProfile = "text" | "mixed";

export interface VideoCodecProfile {
  /** Chemin de transport effectif : `wss` (relais WebSocket, firewall strict)
   *  ou `webrtc` (chemin P2P à la demande). */
  transport: VideoTransportProfile;
  /** Nature du contenu observé : `text` (bureautique/terminal, fixe, texte net)
   *  ou `mixed` (vidéo/animation/contenu changeant). */
  content: VideoContentProfile;
}

/**
 * Matrice profil → codec préféré (avant filtrage par le support réel).
 *
 *   transport \ content │  text                 │  mixed
 *   ────────────────────┼───────────────────────┼──────────────────────────────
 *   wss (firewall/WSS)  │  H265  (compression)  │  H265  (débit borné sur relais)
 *   webrtc (P2P/CPU OK) │  AV1   (contenu fixe) │  H264  (latence/négo robuste)
 *
 * Raison d'être :
 *  - WSS + texte → H265 : sur un chemin relayé (firewall), la priorité est la
 *    COMPRESSION ; HEVC réduit le débit du contenu texte sans surcoût décodage.
 *  - WSS + mixte → H265 : même chemin contraint, on garde HEVC pour borner le
 *    débit ; AV1 software serait trop coûteux et WebRTC n'est pas le transport.
 *  - WebRTC + texte → AV1 : chemin P2P, CPU disponible et contenu FIXE → AV1
 *    maximise la compression d'un écran statique (cas idéal AV1).
 *  - WebRTC + mixte → H264 : on privilégie la latence et une négociation WebRTC
 *    robuste (le transport WebRTC du projet est H264-only, cf. preset webrtc).
 *
 * Le codec préféré est ensuite confronté au support agent+navigateur ; cf.
 * `pickCodecForProfile`.
 */
export function preferredCodecForProfile(profile: VideoCodecProfile): VideoCodecId {
  if (profile.transport === "wss") return "h265";
  // webrtc
  return profile.content === "text" ? "av1" : "h264";
}

/** Ordre de cascade (du plus compressé au plus universel) — utilisé pour
 *  retomber proprement quand le codec préféré n'est pas supporté. H264 ferme
 *  toujours la marche (support garanti via `defaultVideoPreset`). */
const CODEC_CASCADE: VideoCodecId[] = ["av1", "h265", "h264"];

function presetForCodec(codec: VideoCodecId): VideoPreset | undefined {
  // WS-I cible le chemin WebCodecs (le preset webrtc reste sélectionné par le
  // chemin WebRTC dédié). On mappe donc chaque codec sur son preset webcodecs.
  return VIDEO_PRESETS.find((p) => p.codec === codec && p.transport === "webcodecs");
}

/**
 * WS-I — choisit le preset/codec optimal pour un profil, en validant le support.
 *
 * @param profile        Profil réseau+contenu (cf. matrice ci-dessus).
 * @param features       Capabilities agent (`codecH265`/`codecAV1`/…), comme WS-B.
 * @param browserSupport Décodage navigateur par codec (`{ h265, av1 }`), tel que
 *                       calculé par `browserCodecSupport()` côté video.tsx.
 *
 * Algorithme : on part du codec préféré (matrice), puis on descend la cascade
 * `av1 → h265 → h264` à partir de ce codec ; on retient le PREMIER codec dont le
 * preset webcodecs est supporté à la fois par l'agent (`isPresetAgentSupported`)
 * ET par le navigateur (`browserSupport[codec]`, h264 toujours OK). En dernier
 * ressort, `defaultVideoPreset()` (h264) — donc jamais pire que l'actuel.
 *
 * NB : cette fonction ne lit AUCUN flag ; le gating OFF/ON est fait par
 * l'appelant (video.tsx). Appelée = le toggle est déjà ON.
 */
export function pickCodecForProfile(
  profile: VideoCodecProfile,
  features: CodecFeatureMap | null | undefined,
  browserSupport: Partial<Record<VideoCodecId, boolean>> | null | undefined,
): VideoPreset {
  const browserOk = (codec: VideoCodecId): boolean => (codec === "h264" ? true : browserSupport?.[codec] === true);

  const preferred = preferredCodecForProfile(profile);
  // Lot A (reco #2) — contenu TEXTE : on place AV1 en tête de cascade. La feature
  // codecAV1 n'est annoncée que si l'agent possède un encodeur AV1 HARDWARE
  // (native-agent.js : codecAV1 = helperCapabilities.av1 =
  // IsMediaFoundationAv1HardwareAvailable). Couplé au décodage WebCodecs du
  // navigateur, l'AV1 HW offre une meilleure compression à qualité égale → moins
  // de bande passante → permet de lever le clamp 1024px qui floute le texte. Si
  // AV1 indispo (pas de HW, ou navigateur sans WebCodecs AV1), la cascade retombe
  // proprement sur h265 puis h264 : sélection STRICTEMENT identique à l'existant.
  // Le contenu animé garde la préférence transport (webrtc→h264, wss→h265).
  const startCodec: VideoCodecId = profile.content === "text" ? "av1" : preferred;
  const start = CODEC_CASCADE.indexOf(startCodec);
  const cascade = start >= 0 ? CODEC_CASCADE.slice(start) : CODEC_CASCADE;

  for (const codec of cascade) {
    const preset = presetForCodec(codec);
    if (!preset) continue;
    // Réutilise STRICTEMENT la logique de support WS-B (agent) + décodage
    // navigateur — aucune négociation réinventée.
    if (isPresetAgentSupported(preset, features) && browserOk(codec)) {
      return preset;
    }
  }

  return defaultVideoPreset();
}

export interface FreeParams {
  fps: number;
  maxWidth: number;
  bitrate: number;
  colorMode: VideoColorMode;
}

export function buildVideoStartParams(
  preset: VideoPreset,
  profile: QualityProfile,
  free?: FreeParams,
  /**
   * Viewport opérateur en pixels PHYSIQUES. S'il est fourni, le `maxWidth` du
   * profil/des paramètres libres devient un PLAFOND : `min(largeur, viewport.w)`.
   * Le 1er encode est ainsi déjà aligné sur la fenêtre (plus de flou d'upscale).
   * Absent → comportement historique strictement identique.
   */
  viewport?: { w: number; h: number },
): VideoStartParams {
  const isFree = profile.id === "free";
  const src = isFree && free ? free : profile;
  const colorMode = isFree && free ? free.colorMode : (profile.preferColorMode ?? "color");
  const hasViewport = !!viewport && viewport.w > 0 && viewport.h > 0;
  const maxWidth = hasViewport ? Math.min(src.maxWidth, viewport!.w) : src.maxWidth;
  return {
    fps: src.fps,
    maxWidth,
    bitrate: src.bitrate,
    codec: preset.codec,
    mode: preset.mode,
    ...(colorMode !== "color" ? { colorMode } : {}),
    ...(hasViewport ? { viewportW: viewport!.w, viewportH: viewport!.h } : {}),
  };
}
